/**
 * Get merged user data from Cognito and AppSync
 * Cognito stores ONLY email and email_verified
 * All other user data (name, nickname, birthdate, picture, etc.) comes from AppSync
 */

import { CognitoIdentityProviderClient, GetUserCommand } from "@aws-sdk/client-cognito-identity-provider";
import { fetchAuthSession } from 'aws-amplify/auth';
import { getTokensFromCache, getUserDataFromCache, saveUserDataToCache } from './tokenCache';
import { GET_USER } from '../../../services/graphql/queries';
import { isConfigured, appSyncConfig } from '../../../services/config/appSync';

const AWS_REGION = process.env.REACT_APP_AWS_REGION || 'us-west-2';
const cognitoClient = new CognitoIdentityProviderClient({ 
  region: AWS_REGION 
});

/**
 * Cognito user data - only email and verification status
 * All other user profile data is stored in AppSync
 */
export interface CognitoUserData {
  sub: string; // User ID
  email: string;
  email_verified: boolean;
}

export interface MergedUserData {
  // From Cognito
  sub: string; // User ID
  email: string;
  email_verified: boolean;
  // From AppSync
  id?: string;
  birthdate?: string;
  picture_url?: string;
  nickname?: string;
  firstName?: string;
  lastName?: string;
  avatarUrl?: string;
  lastSeenAt?: string;
  createdAt?: string;
}

/**
 * Get user data from Cognito using AccessToken
 * Only retrieves email and email_verified (all other data comes from AppSync)
 * Exported for use in other modules
 */
export const getCognitoUserData = async (accessToken: string): Promise<CognitoUserData | null> => {
  try {
    // Remove Bearer prefix if present
    const token = accessToken.startsWith('Bearer ') ? accessToken.substring(7) : accessToken;
    
    const command = new GetUserCommand({
      AccessToken: token
    });
    
    const response = await cognitoClient.send(command);
    
    if (response.UserAttributes) {
      const attributes = response.UserAttributes;
      
      // Extract attributes
      const emailAttr = attributes.find(attr => attr.Name === 'email');
      const emailVerifiedAttr = attributes.find(attr => attr.Name === 'email_verified');
      
      const cognitoData: CognitoUserData = {
        sub: response.UserAttributes?.find(attr => attr.Name === 'sub')?.Value || '',
        email: emailAttr?.Value || '',
        email_verified: emailVerifiedAttr?.Value === 'true',
      };
      
      console.log('✅ Cognito user data retrieved:', cognitoData);
      return cognitoData;
    }
    
    return null;
  } catch (error: any) {
    console.error('❌ Failed to get Cognito user data:', error);
    return null;
  }
};

/**
 * Get user data from AppSync using Fetch API with access token
 * AppSync requires access token, not ID token
 */
const getAppSyncUserData = async (): Promise<any | null> => {
  try {
    // Ensure Amplify is configured
    if (!isConfigured()) {
      console.error('❌ Amplify is not configured');
      return null;
    }
    
    // Get access token from session (AppSync requires access token, not ID token)
    const session = await fetchAuthSession();
    
    if (!session.tokens?.accessToken) {
      console.error('❌ No access token available');
      return null;
    }

    const accessToken = session.tokens.accessToken.toString();
    
    // Use fetch API with access token (AppSync requires access token)
    const endpoint = appSyncConfig.aws_appsync_graphqlEndpoint;
    if (!endpoint) {
      console.error('❌ AppSync endpoint not configured');
      return null;
    }
    
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`
      },
      body: JSON.stringify({
        query: GET_USER
      })
    });

    if (!response.ok) {
      throw new Error(`AppSync request failed: ${response.statusText}`);
    }

    const result = await response.json();

    if (result.errors) {
      console.error('❌ AppSync GraphQL errors:', result.errors);
      return null;
    }
    
    const appSyncData = result.data?.getUser;
    
    // Handle case where user doesn't exist in AppSync yet
    if (!appSyncData) {
      console.log('⚠️ User not found in AppSync (user may not be created yet)');
      return null;
    }
    
    console.log('✅ AppSync user data retrieved:', appSyncData);
    return appSyncData;
  } catch (error: any) {
    console.error('❌ Failed to get AppSync user data:', error);
    return null;
  }
};

/**
 * Get merged user data from both Cognito and AppSync
 * Only calls APIs if tokens are present and not expired
 */
export const getMergedUserData = async (): Promise<MergedUserData | null> => {
  try {
    // Check if tokens are present and not expired
    const tokens = getTokensFromCache();
    if (!tokens) {
      console.log('⚠️ No valid tokens available, skipping user data fetch');
      return null;
    }
    
    // Extract tokens without Bearer prefix for Cognito API
    const accessToken = tokens.accessToken;
    const idToken = tokens.idToken;
    
    // Try to get user data from cache first
    let cognitoData = getUserDataFromCache();
    
    // If not in cache or expired, fetch from Cognito API
    if (!cognitoData) {
      console.log('📥 Fetching Cognito user data from API...');
      cognitoData = await getCognitoUserData(accessToken);
      
      // Save to cache if successfully fetched
      if (cognitoData) {
        saveUserDataToCache(cognitoData, tokens.expiresAt);
      }
    } else {
      console.log('✅ Using cached Cognito user data');
    }
    if (!cognitoData) {
      console.error('❌ Failed to get Cognito user data');
      return null;
    }
    
    // Call AppSync GraphQL API using Amplify client
    // AppSync will automatically extract user from auth token and set custom headers
    const appSyncData = await getAppSyncUserData();
    
    // Merge responses
    const mergedData: MergedUserData = {
      // From Cognito
      sub: cognitoData.sub,
      email: cognitoData.email,
      email_verified: cognitoData.email_verified,
      // From AppSync (if available) - AppSync data takes precedence
      // GraphQL response uses camelCase, map to MergedUserData format
      ...(appSyncData ? {
        id: appSyncData.id,
        firstName: appSyncData.firstName,
        lastName: appSyncData.lastName,
        nickname: appSyncData.nickname,
        birthdate: appSyncData.birthDate,
        picture_url: appSyncData.pictureUrl,
        lastSeenAt: appSyncData.lastSeenAt,
        createdAt: appSyncData.createdAt,
      } : {})
    };
    
    console.log('✅ Merged user data:', mergedData);
    return mergedData;
  } catch (error: any) {
    console.error('❌ Failed to get merged user data:', error);
    return null;
  }
};

